import React from "react";

// Tiny 28x18 SVG payoff diagrams used by the OptionStrat-style strategy
// picker. Hand-tuned polylines — green where payoff is positive, red where
// negative, white center axis. Compact enough to render hundreds without
// jank, and we don't need exact accuracy: the picker just needs a quick
// visual cue. Falls back to a flat zero line for unknown shapes.

const PROFILES = {
  long_call:        "0,9 14,9 28,2",
  long_put:         "0,2 14,9 28,9",
  covered_call:     "0,14 14,7 28,5",
  csp:              "0,14 14,7 28,7",
  bull_put:         "0,14 10,14 18,5 28,5",
  bear_call:        "0,5 10,5 18,14 28,14",
  bull_call:        "0,14 10,14 18,4 28,4",
  bear_put:         "0,4 10,4 18,14 28,14",
  iron_condor:      "0,14 6,14 10,4 18,4 22,14 28,14",
  iron_butterfly:   "0,14 14,4 28,14",
  butterfly:        "0,12 10,12 14,4 18,12 28,12",
  straddle:         "0,2 14,12 28,2",
  strangle:         "0,4 8,12 20,12 28,4",
  calendar:         "0,14 10,8 14,4 18,8 28,14",
  collar:           "0,12 10,12 18,6 28,6",
  diagonal:         "0,12 10,12 18,4 28,8",
  synthetic_long:   "0,16 28,2",
  ratio_call:       "0,12 14,4 22,12 28,16",
  ratio_put:        "0,16 6,12 14,4 28,12",
  ratio_call_back:  "0,12 14,14 22,8 28,2",
  ratio_put_back:   "0,2 6,8 14,14 28,12",
  bwb:              "0,12 8,12 12,6 18,12 24,14 28,14",
  jade_lizard:      "0,16 10,12 18,4 28,4",
  double_diagonal:  "0,14 8,8 14,4 20,8 28,14",
  box:              "0,9 28,9",
  strap:            "0,4 10,12 14,12 28,2",
  strip:            "0,2 14,12 18,12 28,4",
};

const StrategyDiagram = ({ profile = "long_call", className = "" }) => {
  const pts = PROFILES[profile] || PROFILES.long_call;
  // Split the polyline into red (below zero / y>9) and green (above zero /
  // y<9) segments by linearly interpolating the crossing. Cheap visual,
  // good enough for thumbnails.
  const points = pts.split(" ").map((p) => {
    const [x, y] = p.split(",").map(Number);
    return { x, y };
  });
  const greenSegs = [];
  const redSegs = [];
  let curr = [];
  let currColor = points[0].y <= 9 ? "g" : "r";
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const color = p.y <= 9 ? "g" : "r";
    if (color !== currColor && curr.length) {
      const prev = curr[curr.length - 1];
      // interpolate crossing at y=9
      const t = (9 - prev.y) / (p.y - prev.y || 1);
      const cx = prev.x + (p.x - prev.x) * t;
      curr.push({ x: cx, y: 9 });
      (currColor === "g" ? greenSegs : redSegs).push(curr);
      curr = [{ x: cx, y: 9 }];
      currColor = color;
    }
    curr.push(p);
  }
  if (curr.length) (currColor === "g" ? greenSegs : redSegs).push(curr);

  return (
    <svg
      viewBox="0 0 28 18"
      preserveAspectRatio="none"
      className={`w-7 h-[18px] flex-shrink-0 ${className}`}
      aria-hidden="true"
    >
      <line x1="0" y1="9" x2="28" y2="9" stroke="#ffffff20" strokeWidth="0.5" />
      {greenSegs.map((seg, i) => (
        <polyline
          key={`g-${i}`}
          points={seg.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#22C55E"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {redSegs.map((seg, i) => (
        <polyline
          key={`r-${i}`}
          points={seg.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#EF4444"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

export default StrategyDiagram;
